import React from 'react';
import { useParams } from 'react-router-dom';
import { getSalonByHost } from '../api/storeApi';
import { isDomainMode, getDomainHost } from './domainMode';

/**
 * THE SALON SLUG A PUBLIC PAGE MUST FETCH WITH, IN BOTH DOMAIN MODES.
 *
 * Every public storefront API is addressed BY SLUG —
 * `/public/salons/:salonSlug/products`, `/…/properties/:propertySlug`,
 * `/…/courses/:courseSlug`, `/config/salon-by-slug/:slug`. The ROUTES are not:
 * on the platform they carry the slug (`/:salonSlug/shop`), and on a business's
 * own domain (book.stylon.co and the white-label domains) they are slug-less by
 * design, because the HOST identifies the business (see App.js's two route
 * trees, and `domainMode.js`).
 *
 * So `useParams().salonSlug` is `undefined` on a custom domain, and a page that
 * fetched with it asked for `/public/salons/undefined/…`. What the customer saw
 * depended only on which endpoint it was: "Product not found", "Property not
 * found", an empty shop, an empty course catalogue — every one of them a lie
 * told on the domain the business prints on its own cards, while the identical
 * page worked on stylon.online. CoursePage was fixed first and alone; this is
 * that fix, extracted, so the next page to be written cannot re-open the hole.
 *
 * The host knows: `/config/salon-by-host` is the lookup the app already uses to
 * resolve a branded domain, and the salon it returns carries the slug. Asked
 * ONLY when the route gave no slug AND `isDomainMode()` — which is a positive
 * fact (a salon was actually resolved for this host, recorded in the
 * server-injected boot payload), never a hostname guess. Platform pages
 * therefore make exactly the requests they made before and pay nothing for this.
 *
 * THREE ANSWERS, NOT TWO. A page needs to tell "waiting for the host lookup"
 * from "this host resolves to no business", because they need opposite screens:
 *
 *   salonSlug              the slug to fetch with, or null while there isn't one
 *   resolvingSalonSlug     the lookup is in flight — this is a LOADING state,
 *                          and fetching now with `undefined` would render a
 *                          404 that is really an unfinished request
 *   salonSlugUnresolved    terminal: no business for this host. An honest
 *                          "business not found", never a spinner that never
 *                          ends. (Also true for the impossible platform case of
 *                          a slug-less route outside domain mode, so a routing
 *                          mistake fails closed rather than fetching
 *                          `undefined`.)
 *
 * Note the fallback direction: an UNKNOWN host is not domain mode at all
 * (domainMode.js), so it keeps the slug in the URL and never reaches here —
 * `/shop` on such a host is a platform path and Home answers it with its own
 * "Business not found". `salonSlugUnresolved` covers the narrower race this
 * hook can actually see: a domain that resolved when the HTML was served and
 * no longer does when the browser asks (unlinked, suspended, API blip).
 */
export default function useRouteSalonSlug(callerSlug) {
  const { salonSlug: paramSlug } = useParams();

  // A THIRD SOURCE: the caller already knows the business.
  //
  // `Home` renders <Shop/> and <PropertiesStays/> in place at `/<slug>` so a
  // single-feature business keeps one address with one meaning. Home is routed
  // at `path="*"`, which carries NO params, so those pages asked this hook for
  // a slug the route could not give and got `salonSlugUnresolved` — rendering
  // "Business not found" for a business that plainly exists (/dk-store,
  // /swesosetta, 2026-10-24). An empty string is not a slug: it must not count
  // as knowing, or the shop fetches with nothing.
  const knownSlug = callerSlug || null;

  // A fact about the document, not about the render: `isDomainMode()` reads the
  // boot payload the server stamped in before the bundle ran, so this never
  // flips mid-session.
  const needsHostLookup = !paramSlug && !knownSlug && isDomainMode();

  const [hostSlug, setHostSlug] = React.useState(null);
  const [hostFailed, setHostFailed] = React.useState(false);

  React.useEffect(() => {
    if (!needsHostLookup) return undefined;
    let alive = true;
    getSalonByHost(getDomainHost())
      .then((s) => {
        if (!alive) return;
        const salon = (s && s.salon) || s;
        // `username` is the same string under the older name — `getSalonByHost`
        // emits both, and the existing domain hooks read them in this order.
        const slug = (salon && (salon.slug || salon.username)) || null;
        if (slug) setHostSlug(slug);
        else setHostFailed(true);
      })
      .catch(() => {
        if (alive) setHostFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [needsHostLookup]);

  // The route still wins: `/:salonSlug/shop` behaves exactly as before.
  const salonSlug = paramSlug || knownSlug || hostSlug || null;

  return {
    salonSlug,
    resolvingSalonSlug: !salonSlug && needsHostLookup && !hostFailed,
    salonSlugUnresolved: !salonSlug && (hostFailed || !needsHostLookup),
  };
}
